import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  // aliased — a bare `MouseEvent` would shadow the DOM one the outside-click
  // listener below relies on
  type MouseEvent as ReactMouseEvent,
} from "react";
import { Link } from "react-router-dom";
import {
  SNOOZE_OPTIONS,
  conversationHref,
  copyNumber,
  customerHref,
  waplifyChatHref,
  type SnoozeDuration,
} from "./api";
import type { Severity, WorklistItem } from "./priority";
import { dangerBtn, focusRing, ghostBtn, pillTone, primaryBtn } from "./ui";

/**
 * Severity is carried by three coordinated signals — the left rail, a restrained
 * row wash (critical only), and the status pill. Semantic tokens only (crit/warn/
 * clinic, tailwind.config.js): the interactive accent (action-*) is deliberately a
 * different hue family so a button can never be mistaken for an alarm.
 */
// NOTE ON COLOURS: this theme (Horizon) does NOT ship a `slate` scale — every
// `slate-*` class silently no-ops. It also defines `red-50` as `#ee5d501a` — a
// 10%-ALPHA red, not a pale tint — which is how the old salmon-block bug happened.
// The crit/warn/clinic tokens are real opaque hexes; contrast checked (AA).
// LANDMINE: the tier panel's `divide-clinic-100` sets border-color on `* ~ *` at
// HIGHER specificity than a plain `border-crit-500`, so every row after the first
// silently lost its rail colour. Tailwind 3.1 has no per-side border-colour
// utilities either — hence !important arbitrary properties with the raw token
// hexes (crit-500 / warn-400 / clinic-300 / clinic-700).
const rail: Record<Severity, string> = {
  critical: "border-l-4 ![border-left-color:#dc2626]",
  warning: "border-l-4 ![border-left-color:#f59e0b]",
  muted:
    "border-l-4 ![border-left-color:#cbd5e1] dark:![border-left-color:#334155]",
};

/** Only the top severity earns a tinted row — if everything shouts, nothing does.
    Hover lives here too: a critical row must KEEP its tint under the cursor. */
const wash: Record<Severity, string> = {
  critical: "bg-crit-50 hover:bg-crit-100 dark:!bg-navy-800 dark:hover:!bg-navy-700",
  warning: "bg-white hover:bg-clinic-50 dark:!bg-navy-800 dark:hover:!bg-navy-700",
  muted: "bg-white hover:bg-clinic-50 dark:!bg-navy-800 dark:hover:!bg-navy-700",
};

const urgencyTone: Record<Severity, string> = {
  critical: "text-crit-600 dark:text-red-400",
  warning: "text-warn-600 dark:text-amber-400",
  muted: "text-clinic-500 dark:text-gray-400",
};

// rows live inside an overflow-hidden panel — an offset ring would clip, so inset
const rowFocusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-action-500";

interface Props {
  item: WorklistItem;
  /**
   * Open the Customer-360 for this row. The row hands back its own element so the
   * panel can hand focus BACK to it on close — the CM keeps their place in the queue.
   */
  onOpen: (item: WorklistItem, opener: HTMLElement) => void;
  /** P2 only — P0 conversations have no "done" state we own. */
  onMarkDone?: (item: WorklistItem) => void;
  onSnooze: (item: WorklistItem, duration: SnoozeDuration) => void;
  pending: boolean;
  error: string | null;
  /**
   * The single most urgent row in the queue — the answer to "what do I touch
   * first?". Carries a solid START HERE tag so a 2-second glance lands on it.
   */
  spotlight?: boolean;
}

export default function WorklistCard({
  item,
  onOpen,
  onMarkDone,
  onSnooze,
  pending,
  error,
  spotlight = false,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);

  const menuRef = useRef<HTMLDivElement | null>(null);
  const snoozeBtnRef = useRef<HTMLButtonElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  const href = customerHref(item.mobile);
  const convoHref = conversationHref(item.kind, item.sourceId, item.mobile);
  const waHref = item.tier === "p2" ? waplifyChatHref(item.mobile) : null;

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  // Move focus onto the confirm button so the destructive step is reachable — and
  // dismissable — without a mouse.
  useEffect(() => {
    if (confirming) confirmRef.current?.focus();
  }, [confirming]);

  useEffect(() => {
    if (!copied) return undefined;
    const t = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(t);
  }, [copied]);

  const onWhatsApp = useCallback(() => {
    // Fire-and-forget: the anchor navigates regardless of whether the copy succeeds.
    void copyNumber(item.mobile).then(setCopied);
  }, [item.mobile]);

  /**
   * Roving keyboard triage: ↑/↓ between rows, Enter to open the 360, without a mouse.
   * Enter now opens the PANEL rather than navigating away — that is the whole point of
   * the 360: understand the customer without losing your place in the queue.
   */
  const onCardKeyDown = useCallback(
    (e: KeyboardEvent<HTMLLIElement>) => {
      if (e.target !== e.currentTarget) return; // let inner controls keep their keys
      if (e.key === "Enter") {
        e.preventDefault();
        onOpen(item, e.currentTarget);
        return;
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();
      const cards = Array.from(
        document.querySelectorAll<HTMLElement>('[data-testid="worklist-card"]')
      );
      const i = cards.indexOf(e.currentTarget);
      const next = cards[i + (e.key === "ArrowDown" ? 1 : -1)];
      next?.focus();
    },
    [item, onOpen]
  );

  /**
   * Click the ROW -> open the 360. Clicks that land on a real control (the action
   * buttons, the snooze menu, a deep link) belong to that control and must not ALSO
   * open the panel — otherwise "Mark done" would fire and slide a panel in behind it.
   */
  const onCardClick = useCallback(
    (e: ReactMouseEvent<HTMLLIElement>) => {
      if ((e.target as HTMLElement).closest("a, button, [role='menu']")) return;
      onOpen(item, e.currentTarget);
    },
    [item, onOpen]
  );

  const onMenuKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      setMenuOpen(false);
      snoozeBtnRef.current?.focus();
    }
  }, []);

  return (
    /*
      One dense ROW, edge to edge: rail | scan anchor | identity+signal+context |
      actions pinned right. Rows sit inside the tier panel divided by hairlines,
      so a shift's worth of work fits on one screen.
    */
    <li
      data-testid="worklist-card"
      data-kind={item.kind}
      tabIndex={0}
      onKeyDown={onCardKeyDown}
      onClick={onCardClick}
      aria-haspopup="dialog"
      aria-label={`${spotlight ? "Start here. " : ""}${item.name}. ${item.pill}. ${item.reason}. Open customer details.`}
      className={`group relative flex cursor-pointer items-center gap-4 px-4 py-2.5 transition-colors first:rounded-t-[7px] last:rounded-b-[7px] motion-reduce:transition-none ${rail[item.severity]} ${wash[item.severity]} ${rowFocusRing}`}
    >
      {/*
        The scan anchor. A CM ranking a screenful of rows should not have to read a
        sentence to know which is worst — one tabular number, one label.
      */}
      <div className="w-14 shrink-0 text-center">
        <div
          className={`text-xl font-bold tabular-nums leading-none ${urgencyTone[item.severity]}`}
        >
          {item.urgencyValue}
        </div>
        <div className="mt-1 font-mono text-[9px] font-semibold uppercase leading-tight tracking-[0.12em] text-clinic-500 dark:text-gray-400">
          {item.urgencyLabel}
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          {spotlight && (
            <span
              data-testid="worklist-act-first"
              className="rounded bg-crit-600 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase leading-tight tracking-[0.12em] text-white"
            >
              Start here
            </span>
          )}
          <span className="text-[15px] font-semibold leading-tight text-clinic-900 dark:text-white">
            {item.name}
          </span>
          <span className="text-xs tabular-nums text-clinic-500 dark:text-gray-400">
            {item.mobile}
          </span>
          <span
            className={`rounded px-1.5 py-0.5 text-[11px] font-semibold leading-tight ${pillTone[item.severity]}`}
          >
            {item.pill}
          </span>
        </div>

        {/* line-clamp-2 — tailwind 3.1 has no line-clamp utility, hence the arbitrary props */}
        <p className="mt-1 overflow-hidden text-[13px] leading-snug text-clinic-700 [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box] dark:text-gray-200">
          {item.reason}
        </p>
        <p className="mt-0.5 text-xs tabular-nums leading-snug text-clinic-500 dark:text-gray-400">
          {item.meta}
        </p>

        {error && (
          <p
            role="alert"
            data-testid="worklist-card-error"
            className="mt-1.5 rounded bg-crit-100 px-2 py-1 text-xs font-medium text-crit-700 ring-1 ring-crit-200"
          >
            {error} — the card is unchanged. Retry, or reload the queue.
          </p>
        )}
      </div>

      {/* actions live at the row's right edge — no dead gulf in the middle */}
      <div className="flex shrink-0 items-center gap-1.5">
        {convoHref && (
          <Link
            to={convoHref}
            data-testid="worklist-open-conversation"
            className={primaryBtn}
          >
            Open conversation
          </Link>
        )}

        {/*
          P2 primary. An ANCHOR, not a button — it navigates, it does not act. It
          opens waplify's chat UI in a new tab where a human writes the message.
          Nothing here sends anything. Never rendered on P0: a P0 is an unanswered
          MEDICAL question for a doctor to answer in-app, not for a CM to WhatsApp.
        */}
        {waHref && (
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="worklist-whatsapp"
            onClick={onWhatsApp}
            className={primaryBtn}
          >
            Message on WhatsApp
          </a>
        )}

        {href ? (
          <Link
            to={href}
            data-testid="worklist-view-customer"
            className={ghostBtn}
          >
            View customer
          </Link>
        ) : (
          <button type="button" disabled className={ghostBtn}>
            View customer
          </button>
        )}

        {/*
          Mark done CLOSES a real retention task — a destructive, queue-clearing act
          that used to fire on a single stray click. It is now two-step.
        */}
        {onMarkDone &&
          (confirming ? (
            <span
              role="group"
              aria-label="Confirm mark done"
              className="flex items-center gap-1.5 rounded-md bg-warn-50 px-2 py-1 ring-1 ring-warn-200 dark:!bg-navy-900 dark:ring-navy-700"
            >
              <span className="text-xs font-medium text-warn-700 dark:text-amber-200">
                Close task?
              </span>
              <button
                ref={confirmRef}
                type="button"
                data-testid="worklist-mark-done-confirm"
                disabled={pending}
                onClick={() => {
                  setConfirming(false);
                  onMarkDone(item);
                }}
                className={dangerBtn}
              >
                {pending ? "Working…" : "Yes, mark done"}
              </button>
              <button
                type="button"
                data-testid="worklist-mark-done-cancel"
                onClick={() => setConfirming(false)}
                className={ghostBtn}
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              data-testid="worklist-mark-done"
              disabled={pending}
              onClick={() => setConfirming(true)}
              className={ghostBtn}
            >
              Mark done
            </button>
          ))}

        <div ref={menuRef} onKeyDown={onMenuKeyDown} className="relative">
          <button
            ref={snoozeBtnRef}
            type="button"
            data-testid="worklist-snooze"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            disabled={pending}
            onClick={() => setMenuOpen((open) => !open)}
            className={ghostBtn}
          >
            Snooze <span aria-hidden="true">▾</span>
          </button>

          {menuOpen && (
            <div
              role="menu"
              data-testid="worklist-snooze-menu"
              className="absolute right-0 z-10 mt-1 w-40 overflow-hidden rounded-md bg-white py-1 shadow-lg ring-1 ring-clinic-200 dark:!bg-navy-900 dark:ring-navy-700"
            >
              {SNOOZE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onSnooze(item, opt.value);
                  }}
                  className={`block w-full touch-manipulation px-3 py-1.5 text-left text-xs text-clinic-700 transition-colors motion-reduce:transition-none hover:bg-clinic-100 dark:text-gray-200 dark:hover:bg-navy-700 ${focusRing}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Announced, not just shown — the copy is the part that makes the link useful today. */}
      <span aria-live="polite" className="sr-only">
        {copied ? "Number copied to clipboard" : ""}
      </span>
      {copied && (
        <span
          data-testid="worklist-copied"
          className="pointer-events-none absolute right-3 top-1 rounded bg-clinic-900 px-2 py-0.5 text-[10px] font-medium text-white shadow"
        >
          Number copied
        </span>
      )}
    </li>
  );
}
